import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, MapPin, Users, CurrencyDollar, Sparkle, Envelope, Phone,
  CheckCircle, Buildings, ShieldCheck,
  UserFocus, Warning, Info, LinkedinLogo, InstagramLogo,
  WhatsappLogo, ArrowUpRight, Robot, PaperPlaneRight, Check,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/I18nContext";
import ConversionPanel from "@/components/ConversionPanel";
import ErrorBoundary from "@/components/ErrorBoundary";

const STATUS_LABEL = {
  yeni: "Yeni",
  inceleniyor: "İnceleniyor",
  iletisime_gecildi: "İletişime geçildi",
  nitelikli: "Nitelikli",
  reddedildi: "Reddedildi",
};

export default function LeadDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  // AI Assistant state — chat-style history on the lead detail page.
  // Every answer is grounded in this lead's data via POST /leads/{id}/ask.
  const [askHistory, setAskHistory] = useState([]);   // [{role, text}]
  const [askInput, setAskInput] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const { locale } = useI18n();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/leads/${id}`, { params: searchParams.get("session_id") ? { session_id: searchParams.get("session_id") } : undefined }).then((r) => {
      setLead(r.data);
      setNotes(r.data.notes || "");
      setStatus(r.data.status);
    });
  }, [id, locale, searchParams]);

  const askAssistant = async (question) => {
    const q = (question ?? askInput).trim();
    if (q.length < 3) return;
    setAskInput("");
    setAskHistory((h) => [...h, { role: "user", text: q }]);
    setAskBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/ask`, {
        question: q, language: locale,
      });
      setAskHistory((h) => [...h, { role: "assistant", text: data.answer }]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setAskHistory((h) => [...h, {
        role: "assistant",
        text: typeof detail === "string" ? detail : (
          locale === "en"
            ? "The assistant couldn't be reached — please try again."
            : "Asistana ulaşılamadı — lütfen tekrar deneyin."
        ),
      }]);
    } finally {
      setAskBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${id}`, { notes, status });
      toast.success("Kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  if (!lead) return (
    <div className="p-6 md:p-10 space-y-4">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-64 w-full" />
    </div>
  );

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> {locale === "en" ? "Back to opportunities" : "Fırsatlara dön"}
      </button>

      {/* Header */}
      <div className="rounded-lg border border-border/60 bg-card p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="acas-eyebrow mb-2 flex items-center gap-2">
              {lead.source} • Keşfedildi
              {lead.is_real && (
                <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                  Gerçek Kayıt
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{lead.company_name}</h1>
            <div className="flex flex-wrap gap-2 mt-3">
              {lead.qualification?.relationship_type && (
                <Badge variant="outline" data-testid="lead-commercial-role">
                  {lead.qualification.relationship_type === "reseller_sales_point" ? "Satış noktası / bayi"
                    : lead.qualification.relationship_type === "sponsor_prospect" ? "Sponsor adayı"
                    : lead.qualification.relationship_type === "event_participant_prospect" ? "Etkinlik katılımcısı adayı"
                    : lead.qualification.relationship_type === "personal_b2c_service_provider" ? "Kişisel hizmet sağlayıcısı"
                    : lead.qualification.relationship_type === "audience_access_channel" ? "Hedef kitle erişim kanalı"
                    : "B2B satış adayı"}
                </Badge>
              )}
              <Badge variant="outline"><Buildings size={12} className="mr-1" /> {lead.industry}</Badge>
              <Badge variant="outline"><MapPin size={12} className="mr-1" /> {lead.location}</Badge>
              {lead.employee_estimate && <Badge variant="outline"><Users size={12} className="mr-1" /> {lead.employee_estimate}</Badge>}
              {lead.revenue_estimate && <Badge variant="outline"><CurrencyDollar size={12} className="mr-1" /> Ciro: {lead.revenue_estimate}</Badge>}
              {lead.rating != null && <Badge variant="outline">★ {Number(lead.rating).toFixed(1)} ({lead.review_count || 0})</Badge>}
            </div>
            {lead.address && <div className="text-xs text-muted-foreground mt-2">{lead.address}</div>}
          </div>

          <div className="rounded-md border border-primary/20 bg-primary/5 p-4 min-w-[200px]">
            <div className="acas-eyebrow text-primary/80">{locale === "en" ? "Lead Score" : "Lead Skoru"}</div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="text-5xl font-extrabold text-primary leading-none">{lead.match_score}</div>
              <div className="text-xs text-muted-foreground">/100</div>
            </div>
            <div className="mt-2 text-sm font-semibold" data-testid="lead-quality-label">
              {(() => {
                const s = lead.match_score || 0;
                if (s >= 75) return locale === "en" ? "High Match" : "Yüksek Uyum";
                if (s >= 55) return locale === "en" ? "Good Match" : "İyi Uyum";
                if (s >= 35) return locale === "en" ? "Moderate Match" : "Orta Uyum";
                return locale === "en" ? "Low Match" : "Düşük Uyum";
              })()}
              {" · "}
              <span className="text-primary">
                {"★".repeat(Math.max(1, Math.min(5, Math.round((lead.match_score || 0) / 20))))}
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-primary/10 overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${lead.match_score}%` }} />
            </div>
            {lead.priority && (
              <div className="mt-3 flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold border ${
                  lead.priority === "high" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                  lead.priority === "medium" ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
                  "border-border bg-muted/40 text-muted-foreground"
                }`} data-testid="lead-priority">
                  {locale === "en"
                    ? (lead.priority === "high" ? "High priority" : lead.priority === "medium" ? "Medium" : "Low")
                    : (lead.priority === "high" ? "Yüksek öncelik" : lead.priority === "medium" ? "Orta öncelik" : "Düşük öncelik")}
                </span>
              </div>
            )}
            {lead.confidence != null && (
              <div className="mt-2" data-testid="lead-confidence">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                  <span><ShieldCheck size={10} className="inline mr-1" />{locale === "en" ? "Confidence" : "Güven"}</span>
                  <span className="mono">%{Math.round(lead.confidence * 100)}</span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-muted/40 overflow-hidden">
                  <div className="h-full bg-emerald-500/60" style={{ width: `${Math.round(lead.confidence * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main - Why AI picked */}
        <div className="lg:col-span-2 space-y-5">
          {/* Conversion Engine — the primary "what do I do with this?" panel */}
          <Section title={locale === "en" ? "Conversion" : "Dönüşüm"} icon={Sparkle} accent>
            <ErrorBoundary label="conversion-panel" locale={locale}>
              <ConversionPanel lead={lead} locale={locale} onLeadUpdate={setLead} />
            </ErrorBoundary>
          </Section>

          <Section title={locale === "en" ? "Why AI picked this lead" : "AI neden bu firmayı seçti?"} icon={Sparkle}>
            <p className="text-sm leading-relaxed">{lead.reason}</p>
            <ScoreBreakdown lead={lead} locale={locale} />
          </Section>

          <CommercialEvidenceCard lead={lead} />

          <ContactStrategyCard lead={lead} locale={locale} />

          <Section title={locale === "en" ? "Ask Next io about this lead" : "Next io'a bu firmayı sor"} icon={Robot}>
            <div className="space-y-3">
              {askHistory.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  {locale === "en"
                    ? "Ask questions grounded in what Next io actually collected about this lead. The assistant will never invent data."
                    : "Sadece bu firma hakkında Next io'ın topladığı gerçek verilere dayanan cevaplar alırsınız. Asistan uydurma bilgi vermez."}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { tr: "Bu firmayı neden önerdin?", en: "Why did you recommend this lead?" },
                  { tr: "Skor neden bu kadar yüksek?", en: "Why is the score high?" },
                  { tr: "Bu öneriyi destekleyen kanıt nedir?", en: "What evidence supports this recommendation?" },
                  { tr: "Bu işletmenin zayıf yönleri neler?", en: "What weaknesses does this business have?" },
                  { tr: "İlk önce bu firmayla mı iletişime geçmeliyim?", en: "Should I contact this business first?" },
                ].map((q) => (
                  <button
                    key={q.en}
                    type="button"
                    onClick={() => askAssistant(locale === "en" ? q.en : q.tr)}
                    disabled={askBusy}
                    data-testid={`ask-chip-${q.en.replace(/[^a-z]/gi, "").slice(0, 20)}`}
                    className="text-[11px] px-2 py-1 rounded-full border border-border/60 hover:border-primary/50 hover:bg-primary/[0.04] transition disabled:opacity-50"
                  >
                    {locale === "en" ? q.en : q.tr}
                  </button>
                ))}
              </div>
              {askHistory.length > 0 && (
                <div className="rounded-md border border-border/50 bg-muted/20 p-3 space-y-2 max-h-96 overflow-y-auto" data-testid="ask-history">
                  {askHistory.map((m, i) => (
                    <div key={i} className={`text-sm flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
                      {m.role === "assistant" && (
                        <Robot size={14} weight="fill" className="text-primary flex-shrink-0 mt-0.5" />
                      )}
                      <div className={`max-w-[85%] rounded-md px-3 py-2 whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-primary/10 text-foreground"
                          : "bg-card border border-border/50"
                      }`}>{m.text}</div>
                    </div>
                  ))}
                  {askBusy && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Sparkle size={11} weight="fill" className="animate-pulse text-primary" />
                      {locale === "en" ? "Thinking…" : "Düşünüyor…"}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={askInput}
                  onChange={(e) => setAskInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !askBusy) askAssistant(); }}
                  placeholder={locale === "en" ? "Ask anything — grounded only in this lead's evidence" : "Soru yazın — yalnızca bu firmanın kanıtlarına göre yanıtlanır"}
                  disabled={askBusy}
                  data-testid="ask-input"
                />
                <Button
                  onClick={() => askAssistant()}
                  disabled={askBusy || askInput.trim().length < 3}
                  data-testid="ask-submit"
                  className="gap-1.5"
                >
                  <PaperPlaneRight size={14} weight="fill" />
                  {locale === "en" ? "Ask" : "Sor"}
                </Button>
              </div>
            </div>
          </Section>

          <Section title={locale === "en" ? "Action plan" : "Aksiyon Planı"} icon={CheckCircle}>
            <ActionPlanChecklist lead={lead} locale={locale} onLeadUpdate={setLead} />
          </Section>

          <Section title={locale === "en" ? "Your notes" : "Notlarınız"} icon={Envelope}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                      data-testid="lead-notes"
                      placeholder={locale === "en" ? "Internal notes for your team…" : "İç ekip notlarınızı buraya yazın…"} />
          </Section>
        </div>

        {/* Right - contact + status */}
        <div className="space-y-5">
          <Section title="İletişim">
            <div className="space-y-2 text-sm">
              {lead.website && (
                <a href={lead.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-primary" data-testid="lead-website">
                  <Globe size={14} /> <span className="mono truncate">{lead.website}</span>
                </a>
              )}
              {lead.google_maps_url && (
                <a href={lead.google_maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-primary" data-testid="lead-gmaps">
                  <MapPin size={14} /> <span className="mono truncate">Google Maps'te aç</span>
                </a>
              )}
              {lead.contact_email && (
                <div className="flex items-center gap-2"><Envelope size={14} /> <span className="mono">{lead.contact_email}</span></div>
              )}
              {lead.contact_phone && (
                <div className="flex items-center gap-2"><Phone size={14} /> <span className="mono">{lead.contact_phone}</span></div>
              )}
            </div>
          </Section>

          <Section title="Durum">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="lead-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={save} disabled={saving} className="w-full mt-3" data-testid="save-lead">
              {saving ? "Kaydediliyor…" : "Değişiklikleri kaydet"}
            </Button>
          </Section>

        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score Breakdown — parses the ` · `-separated reason string emitted by
// ---------------------------------------------------------------------------
function CommercialEvidenceCard({ lead }) {
  const q = lead.qualification;
  if (!q) return null;
  const label = q.evidence_level === "strong" ? "Güçlü kanıt" : q.evidence_level === "partial" ? "Kısmi kanıt" : "Yetersiz kanıt";
  return (
    <Section title="Ticari Uygunluk" icon={ShieldCheck}>
      <div className="space-y-4" data-testid="commercial-evidence-card">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={q.evidence_level === "strong" ? "default" : "outline"} data-testid="commercial-evidence-level">{label}</Badge>
          <Badge variant="outline" data-testid="candidate-commercial-role">
            {q.candidate_role === "retailer_or_reseller" ? "Perakende / yeniden satış" : q.candidate_role === "operational_user" ? "Operasyonel kullanım" : q.candidate_role === "producer_or_manufacturer" ? "Üretici / imalatçı" : "Ticari rol belirsiz"}
          </Badge>
        </div>
        <div className="text-sm leading-relaxed" data-testid="commercial-fit-reason"><strong>Neden uygun?</strong> {q.why_suitable}</div>
        <div>
          <div className="acas-eyebrow mb-2">Doğrulanan bilgiler</div>
          {(q.verified_facts || []).length ? (
            <ul className="space-y-1.5 text-sm" data-testid="verified-facts-list">
              {q.verified_facts.slice(0, 8).map((fact, index) => (
                <li key={`${fact.kind}-${index}`} className="flex flex-wrap gap-1.5">
                  <span>{fact.value}</span>
                  {fact.source_url && fact.source_url.startsWith("http") && (
                    <a href={fact.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" data-testid={`evidence-source-${index}`}>Kaynak</a>
                  )}
                </li>
              ))}
            </ul>
          ) : <div className="text-sm text-muted-foreground" data-testid="verified-facts-empty">Doğrulanmış aday bilgisi bulunamadı.</div>}
        </div>
        <div>
          <div className="acas-eyebrow mb-2">Doğrulanamayan bilgiler</div>
          <ul className="space-y-1 text-sm text-muted-foreground" data-testid="commercial-unknowns-list">
            {(q.unknowns || ["Ek bilgi doğrulanamadı."]).slice(0, 5).map((item, index) => <li key={index}>• {item}</li>)}
          </ul>
        </div>
        {q.competitor_conflict && q.competitor_conflict.status !== "none" && (
          <div className="text-xs border-l-2 border-amber-500/60 pl-3 text-muted-foreground" data-testid="competitor-conflict-status">
            <strong>Ticari çatışma kontrolü:</strong> {q.competitor_conflict.reason}
          </div>
        )}
      </div>
    </Section>
  );
}

// Action Plan Checklist — persistent, channel-based checklist on the lead.
// Steps come from backend (/leads/{id}) — action_plan is always populated
// on first fetch. Toggling calls POST /leads/{id}/action-plan/toggle.
// ---------------------------------------------------------------------------
const ACTION_LABELS = {
  research_done:       { tr: "Araştırma tamamlandı",              en: "Research done" },
  outreach_draft_ready:{ tr: "İlk temas taslağı hazır",           en: "Outreach draft ready" },
  email_sent:          { tr: "E-posta gönderildi",                 en: "Email sent" },
  phone_called:        { tr: "Aranıldı",                           en: "Called" },
  whatsapp_sent:       { tr: "WhatsApp mesajı gönderildi",         en: "WhatsApp message sent" },
  instagram_dm_sent:   { tr: "Instagram DM gönderildi",            en: "Instagram DM sent" },
  followup_scheduled:  { tr: "Takip planlandı",                    en: "Follow-up scheduled" },
  response_received:   { tr: "Yanıt alındı",                       en: "Response received" },
};

function ActionPlanChecklist({ lead, locale, onLeadUpdate }) {
  const [busy, setBusy] = useState(null); // key currently toggling
  const plan = lead.action_plan || [];

  if (plan.length === 0) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="action-plan-empty">
        {locale === "en"
          ? "No verified contact channels yet. Once Next io finds an email, phone, WhatsApp or Instagram profile for this lead, a step-by-step plan will appear here."
          : "Henüz doğrulanmış bir iletişim kanalı yok. Next io bir e-posta, telefon, WhatsApp veya Instagram profili bulduğunda buraya adım adım bir plan gelecek."}
      </div>
    );
  }

  const toggle = async (step) => {
    setBusy(step.key);
    try {
      const { data } = await api.post(`/leads/${lead.id}/action-plan/toggle`, {
        key: step.key,
        done: !step.done,
      });
      onLeadUpdate({ ...lead, action_plan: data.action_plan });
    } catch (e) {
      toast.error(locale === "en" ? "Could not update" : "Güncellenemedi");
    } finally {
      setBusy(null);
    }
  };

  const doneCount = plan.filter((s) => s.done).length;
  const pct = Math.round((doneCount / plan.length) * 100);

  return (
    <div data-testid="action-plan-checklist" className="space-y-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {locale === "en"
            ? `${doneCount} of ${plan.length} steps complete`
            : `${plan.length} adımdan ${doneCount} tamamlandı`}
        </span>
        <span className="mono font-semibold text-primary">%{pct}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="space-y-1.5 mt-3">
        {plan.map((step, i) => {
          const label = ACTION_LABELS[step.key];
          const text = label ? (locale === "en" ? label.en : label.tr) : step.key;
          const isBusy = busy === step.key;
          return (
            <li key={step.key}>
              <button
                type="button"
                onClick={() => toggle(step)}
                disabled={isBusy}
                data-testid={`action-plan-item-${step.key}`}
                className={`w-full text-left rounded-md border p-2.5 flex items-start gap-3 transition ${
                  step.done
                    ? "border-emerald-500/25 bg-emerald-500/[0.04]"
                    : "border-border/60 bg-card hover:border-primary/40"
                } ${isBusy ? "opacity-60" : ""}`}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex-shrink-0 grid place-items-center transition ${
                    step.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-border"
                  }`}
                >
                  {step.done && <Check size={12} weight="bold" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium ${step.done ? "line-through text-muted-foreground" : ""}`}>
                    {i + 1}. {text}
                  </div>
                  {step.done && step.done_at && (
                    <div className="text-[10px] text-muted-foreground mono mt-0.5">
                      {new Date(step.done_at).toLocaleString(locale === "en" ? "en-US" : "tr-TR", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Dynamic Next Actions — builds a step-by-step outreach plan grounded in the
// channels Next io actually verified for this lead. Never suggests a channel
// the lead doesn't have (no fake "Call today" without a phone number).
// ---------------------------------------------------------------------------
function NextActionsPlan({ lead, locale }) {
  const en = locale === "en";
  const cs = lead.contact_strategy || {};
  const hasPhone = !!lead.contact_phone;
  const hasEmail = !!lead.contact_email;
  const hasWA = !!(lead.whatsapp && lead.whatsapp.verified);
  const hasIG = !!(lead.instagram && lead.instagram.verified);
  const topChannel = (cs.top_channel || "").toLowerCase();

  // Build the plan in evidence order. Each step is either concrete
  // (channel-backed) or a decision node ("wait 24h — if no reply…").
  const steps = [];
  const t = (tr, e) => (en ? e : tr);

  const primary = topChannel || (hasPhone ? "phone" : hasEmail ? "email" : hasWA ? "whatsapp" : hasIG ? "instagram" : "website");

  const stepFor = (ch) => {
    if (ch === "phone" && hasPhone) return t(`Bugün ara — ${lead.contact_phone}`, `Call today — ${lead.contact_phone}`);
    if (ch === "email" && hasEmail) return t(`E-posta gönder — ${lead.contact_email}`, `Send email — ${lead.contact_email}`);
    if (ch === "whatsapp" && hasWA) return t("WhatsApp üzerinden ilk mesajı at", "Send first WhatsApp message");
    if (ch === "instagram" && hasIG) return t(`Instagram DM (@${lead.instagram.handle})`, `Instagram DM (@${lead.instagram.handle})`);
    return null;
  };

  // Primary step.
  const primaryStep = stepFor(primary);
  if (primaryStep) steps.push({ kind: "action", text: primaryStep });

  // Wait node.
  steps.push({ kind: "wait", text: t("24 saat bekle", "Wait 24 hours") });

  // Fallback channels in priority order.
  const fallbackOrder = ["email", "whatsapp", "instagram", "phone"];
  for (const ch of fallbackOrder) {
    if (ch === primary) continue;
    const s = stepFor(ch);
    if (s) {
      steps.push({ kind: "decision", text: t("Yanıt yoksa", "If no response") });
      steps.push({ kind: "action", text: s });
    }
    if (steps.filter((x) => x.kind === "action").length >= 3) break;
  }

  // Final cold-lead step.
  steps.push({ kind: "wait", text: t("3 gün bekle", "Wait 3 days") });
  steps.push({ kind: "final", text: t("Yanıt yoksa: Soğuk fırsat olarak işaretle", "If no reply: mark as Cold Lead") });

  if (steps.filter((s) => s.kind === "action").length === 0) {
    // Nothing to do — no verified channel exists. Say so plainly.
    return (
      <div className="text-sm text-muted-foreground" data-testid="next-actions-empty">
        {en
          ? "No verified contact channels yet. Once Next io finds an email, phone, WhatsApp or Instagram profile for this lead, a step-by-step plan will appear here."
          : "Henüz doğrulanmış bir iletişim kanalı yok. Next io bir e-posta, telefon, WhatsApp veya Instagram profili bulduğunda buraya adım adım bir plan gelecek."}
      </div>
    );
  }

  return (
    <ol className="space-y-2" data-testid="next-actions-plan">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className={`w-7 h-7 rounded-full grid place-items-center flex-shrink-0 text-[11px] font-bold mono ${
            s.kind === "action" ? "bg-primary/10 text-primary" :
            s.kind === "final" ? "bg-rose-500/10 text-rose-500" :
            "bg-muted text-muted-foreground"
          }`}>
            {s.kind === "wait" ? "◔" : s.kind === "final" ? "✕" : (steps.slice(0, i + 1).filter((x) => x.kind === "action").length)}
          </div>
          <div className={`text-sm ${s.kind === "action" ? "font-semibold" : "text-muted-foreground"}`}>
            {s.text}
          </div>
        </li>
      ))}
    </ol>
  );
}



// intent_scorer.py, categorises each signal, and shows a positive/negative
// list. The scorer never emits negative signals today, but the UI supports
// them so future signals like "-5 Low social activity" render correctly
// out of the box.
// ---------------------------------------------------------------------------
function ScoreBreakdown({ lead, locale }) {
  const [open, setOpen] = React.useState(false);
  const raw = (lead.reason || "").split(" · ").map((s) => s.trim()).filter(Boolean);
  if (raw.length === 0) return null;
  const categorize = (s) => {
    // Rough +/- categorisation. Anything starting with a "-" or with the
    // literal marker "Konu uyumu yok / No topical match" is negative.
    if (/^-\d/.test(s)) return { sign: "-", text: s };
    if (/Konu uyumu yok|No topical match|Insufficient evidence|Yeterli kanıt/.test(s)) {
      return { sign: "-", text: s };
    }
    return { sign: "+", text: s };
  };
  const items = raw.map(categorize);
  return (
    <div className="mt-4" data-testid="score-breakdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-medium text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="score-breakdown-toggle"
      >
        <span>{locale === "en" ? "Score breakdown" : "Skor detayı"}</span>
        <span className="mono">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
          {items.map((it, i) => (
            <div
              key={i}
              className={`px-2 py-1 rounded border flex items-start gap-1.5 ${
                it.sign === "+"
                  ? "border-emerald-500/25 bg-emerald-500/[0.04]"
                  : "border-rose-500/25 bg-rose-500/[0.04]"
              }`}
            >
              <span className={`font-bold mono ${
                it.sign === "+" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
              }`}>{it.sign}</span>
              <span className="text-foreground/90">{it.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function Section({ title, icon: Icon, children, accent }) {
  return (
    <div className={`rounded-lg border p-5 ${accent ? "border-primary/30 bg-primary/[0.03] acas-beam" : "border-border/60 bg-card"}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} weight="duotone" className={accent ? "text-primary" : "text-muted-foreground"} />}
        <h3 className="text-sm font-bold tracking-tight uppercase text-muted-foreground">{title}</h3>
      </div>
      {children}
    </div>
  );
}

const CHANNEL_ICON = {
  direct_email: Envelope,
  general_email: Envelope,
  phone: Phone,
  website_form: Globe,
  linkedin: LinkedinLogo,
  instagram: InstagramLogo,
  whatsapp: WhatsappLogo,
};

function VerificationBadge({ verification, locale }) {
  const isVerified = verification === "verified";
  const label = isVerified
    ? (locale === "en" ? "Verified" : "Doğrulanmış")
    : (locale === "en" ? "AI Recommendation" : "AI Önerisi");
  return (
    <span
      data-testid={`verification-${verification}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
        isVerified
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "border-primary/40 bg-primary/10 text-primary"
      }`}
    >
      {isVerified ? <ShieldCheck size={10} weight="fill" /> : <Sparkle size={10} weight="fill" />}
      {label}
    </span>
  );
}

function ContactStrategyCard({ lead, locale }) {
  const cs = lead.contact_strategy;
  if (!cs) return null;
  const dm = cs.decision_maker || {};
  const channels = cs.channels || [];
  const isEn = locale === "en";

  return (
    <div
      data-testid="contact-strategy-card"
      className="rounded-lg border border-border/60 bg-card p-5 space-y-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <UserFocus size={16} weight="duotone" className="text-primary" />
          <h3 className="text-sm font-bold tracking-tight uppercase text-muted-foreground">
            {isEn ? "Decision Maker & Contact Strategy" : "Karar Verici ve İletişim Stratejisi"}
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground italic">
          {cs.philosophy_note}
        </div>
      </div>

      {/* Decision maker block */}
      <div
        data-testid="decision-maker-block"
        className={`rounded-md border p-3 ${
          dm.status === "none"
            ? "border-border/60 bg-muted/20"
            : dm.status === "verified"
              ? "border-emerald-500/30 bg-emerald-500/[0.04]"
              : "border-primary/30 bg-primary/[0.04]"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          {dm.status === "none" ? (
            <Warning size={14} className="text-muted-foreground" />
          ) : dm.status === "verified" ? (
            <ShieldCheck size={14} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
          ) : (
            <Sparkle size={14} weight="fill" className="text-primary" />
          )}
          <div className="text-xs font-semibold uppercase tracking-wider" data-testid="decision-maker-status">
            {dm.status === "verified"
              ? (isEn ? "Verified decision-maker" : "Doğrulanmış karar verici")
              : dm.status === "ai_inferred"
                ? (isEn ? "AI Recommendation (inference)" : "AI Önerisi (çıkarım)")
                : (isEn ? "No decision-maker identified" : "Karar verici tespit edilemedi")}
          </div>
        </div>

        {dm.status !== "none" ? (
          <>
            {(dm.role_label || dm.person_name_guess) && (
              <div className="text-sm">
                {dm.person_name_guess && (
                  <span className="font-semibold">{dm.person_name_guess}</span>
                )}
                {dm.person_name_guess && dm.role_label && <span className="text-muted-foreground"> — </span>}
                {dm.role_label && <span>{dm.role_label}</span>}
              </div>
            )}
            {typeof dm.confidence === "number" && (
              <div className="mt-2 flex items-center gap-2" data-testid="dm-confidence">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {isEn ? "Confidence" : "Güven"}
                </span>
                <div className="flex-1 h-1 rounded-full bg-muted/40 overflow-hidden max-w-[160px]">
                  <div
                    className="h-full bg-primary/70"
                    style={{ width: `${Math.round(dm.confidence * 100)}%` }}
                  />
                </div>
                <span className="mono text-[10px]">%{Math.round(dm.confidence * 100)}</span>
              </div>
            )}
            {dm.reasoning && (
              <div className="text-xs text-muted-foreground italic mt-2 border-l-2 border-primary/40 pl-2">
                {dm.reasoning}
              </div>
            )}
            {Array.isArray(dm.evidence) && dm.evidence.length > 0 && (
              <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                {dm.evidence.map((e, i) => (
                  <li key={i} className="flex gap-1.5">
                    <Info size={12} className="mt-0.5 flex-shrink-0 text-muted-foreground/70" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">
            {dm.message || (isEn ? "No decision-maker identified." : "Karar verici tespit edilemedi.")}
          </div>
        )}
      </div>

      {/* Channel ranking */}
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          {isEn ? "Recommended communication channels" : "Önerilen iletişim kanalları"}
        </div>
        {channels.length === 0 ? (
          <div className="text-sm text-muted-foreground italic" data-testid="no-channels">
            {isEn
              ? "No verifiable public communication channels found for this lead."
              : "Bu firma için doğrulanabilir açık iletişim kanalı bulunamadı."}
          </div>
        ) : (
          <ol className="space-y-2" data-testid="channel-list">
            {channels.map((c) => {
              const Icon = CHANNEL_ICON[c.channel] || Envelope;
              return (
                <li
                  key={c.channel + c.value}
                  data-testid={`channel-${c.channel}`}
                  className={`flex items-start gap-3 rounded-md border p-3 ${
                    c.rank === 1 ? "border-primary/40 bg-primary/[0.04]" : "border-border/60"
                  }`}
                >
                  <div className={`w-7 h-7 rounded-full grid place-items-center flex-shrink-0 text-xs font-bold ${
                    c.rank === 1 ? "bg-primary text-primary-foreground" : "bg-muted/60 text-muted-foreground"
                  }`}>
                    {c.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon size={14} weight="duotone" className={c.channel === "whatsapp" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"} />
                      <span className="text-sm font-semibold">{c.label}</span>
                      <VerificationBadge verification={c.verification} locale={locale} />
                      {c.is_generic && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-border bg-muted/40 text-muted-foreground">
                          {isEn ? "Generic inbox" : "Genel gelen kutusu"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs mono mt-1 text-muted-foreground break-all">
                      {c.value}
                    </div>
                    {c.reasoning && (
                      <div className="text-xs text-muted-foreground italic mt-1">
                        {c.reasoning}
                      </div>
                    )}
                    {c.channel === "whatsapp" && c.wa_me && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <a
                          href={c.wa_me}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`open-in-whatsapp`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-md border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2.5 h-8 transition"
                        >
                          <WhatsappLogo size={13} weight="fill" />
                          {isEn ? "Open in WhatsApp" : "WhatsApp'ta Aç"}
                          <ArrowUpRight size={11} />
                        </a>
                        {c.evidence_source && (
                          <span className="text-[10px] text-muted-foreground italic" data-testid="whatsapp-source">
                            {isEn ? "Source: " : "Kaynak: "}{c.evidence_source}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

